import { useCallback, useMemo, useState } from "react"
import {
  Button,
  Card,
  CardBody,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Badge,
  FormGroup,
  Label,
  Collapse,
  Row,
  Col,
} from "reactstrap"
import { toast } from "react-toastify"
import { addDays, format, parseISO } from "date-fns"
import { downloadAndOpenFile } from "api/pdfApi"
import { useAllCompanyPads } from "hooks/useCompanyPad"
import {
  useGenerateSupportive,
  useUpdateSupportiveQuotation,
  useDeleteSupportiveQuotation,
  useSupportiveLoan,
  useUpdateSupportiveLoan,
} from "hooks/useQuotation"
import { useToastMessage } from "hooks/useToastMessage"
import { convertToWords } from "helpers/number_helpers"
import ButtonTooltip from "components/Common/ButtonTooltip"
import SupportiveLoanAddEdit from "./SupportiveLoanAddEdit"

// All variants say the same thing: full payment before delivery. One is picked
// at random per company so two supportive quotations don't read identically.
const PAYMENT_TERMS_POOL = [
  "Full payment must be made before vehicle delivery.",
  [
    "Booking is secured upon receipt of the agreed advance amount.",
    "The outstanding balance must be cleared before the vehicle is released.",
    "Partial or pending payments are not eligible for vehicle delivery.",
    "Cash and bank transfer facilities are available.",
  ].join("\n"),
  [
    "An advance deposit is mandatory for booking confirmation and vehicle allocation.",
    "The booking remains subject to payment of the full outstanding amount before delivery.",
    "Delivery authorization will be issued only after the complete payment has been received and verified.",
    "Payments may be completed through cash or bank transfer.",
  ].join("\n"),
]

const DEFAULT_PAYMENT_TERMS = PAYMENT_TERMS_POOL[0]

const pickPaymentTerms = (seen) => {
  const fresh = PAYMENT_TERMS_POOL.filter((t) => !seen || !seen.has(t))
  const pool = fresh.length ? fresh : PAYMENT_TERMS_POOL
  const choice = pool[Math.floor(Math.random() * pool.length)]
  if (seen) {
    seen.add(choice)
  }
  return choice
}

const VALIDITY_MODES = [
  { value: "1_month", label: "1 month" },
  { value: "30_days", label: "30 days" },
  { value: "specific_date", label: "Specific date" },
]

const VALIDITY_CHIPS = [
  { label: "+1 Week", days: 7 },
  { label: "+1 Month", days: 30 },
  { label: "+2 Months", days: 60 },
]

const DEFAULT_PRICE_ITEMS = () => [
  { id: cryptoId(), description: "Unit Price", amount: 0 },
]

const PRICE_BUMP_MIN = 30000
const PRICE_BUMP_MAX = 60000
const PRICE_BUMP_STEP = 5000

const BUMP_VALUES = (() => {
  const values = []
  for (let v = PRICE_BUMP_MIN; v <= PRICE_BUMP_MAX; v += PRICE_BUMP_STEP) {
    values.push(v)
  }
  return values
})()

const randomBump = () =>
  BUMP_VALUES[Math.floor(Math.random() * BUMP_VALUES.length)]

const distinctBumps = (count) => {
  const pool = [...BUMP_VALUES]
  for (let i = pool.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[pool[i], pool[j]] = [pool[j], pool[i]]
  }
  return Array.from({ length: count }, (_, i) =>
    pool.length ? pool[i % pool.length] : randomBump()
  )
}

const sumPriceItems = (items = []) =>
  items.reduce((sum, p) => sum + (Number(p?.amount) || 0), 0)

const totalWithRegistration = (items, registrationCost) =>
  sumPriceItems(items) + (Number(registrationCost) > 0 ? Number(registrationCost) : 0)

const amountToWords = (total) => {
  const n = Math.round(Number(total) || 0)
  return n > 0 ? `${convertToWords(n)} Taka Only` : ""
}

const getParentRegistrationCost = (quotation) => {
  const pdf = quotation?.pdf_content || {}
  const items = Array.isArray(pdf.price_items) ? pdf.price_items : []
  const match = items.find(p => {
    const desc = String(p?.description ?? "").toLowerCase()
    return desc.includes("registration")
  })
  return Number(match?.amount) || 0
}

const isRegistrationItem = (item) =>
  String(item?.description ?? "").toLowerCase().includes("registration")

const buildBumpedPriceItems = (quotation, bump = randomBump()) => {
  const parentItems = quotation?.pdf_content?.price_items
  const source = Array.isArray(parentItems) && parentItems.length > 0
    ? parentItems.filter(item => !isRegistrationItem(item))
    : null

  if (source && source.length > 0) {
    return source.map((item, idx) => ({
      id: cryptoId(),
      description: item?.description ?? (idx === 0 ? "Unit Price" : ""),
      amount: Number(item?.amount || 0) + bump,
    }))
  }

  const fallbackTotal = Number(quotation?.pdf_content?.total_amount || 0)
  if (fallbackTotal > 0) {
    return [
      {
        id: cryptoId(),
        description: "Unit Price",
        amount: fallbackTotal + bump,
      },
    ]
  }

  return DEFAULT_PRICE_ITEMS()
}

const VEHICLE_SPEC_FIELDS = [
  "Vehicle Name",
  "Chassis No",
  "Engine No",
  "Model",
  "Color",
  "CC",
  "Fuel Type",
  "Descriptions",
]

const findSpecValue = (specs = [], key) => {
  const match = specs.find(s => String(s?.key ?? "").toLowerCase() === key.toLowerCase())
  return match ? String(match.value ?? "") : ""
}

const buildVehicleSpecs = (source = []) =>
  VEHICLE_SPEC_FIELDS.map(key => ({
    key,
    value: findSpecValue(source, key),
    type: "text",
  }))

const randomDigit = () => String(Math.floor(Math.random() * 10))

const regenChar = (ch) => (/[0-9]/.test(ch) ? randomDigit() : ch)

const randomizeDigits = (s) => s.replace(/\d/g, randomDigit)

const regenerateIdentifier = (value) => {
  const v = String(value ?? "").trim()
  if (!v) return v
  const dash = v.indexOf("-")
  const regen = (s) => s.split("").map(regenChar).join("")
  return dash === -1 ? regen(v) : v.slice(0, dash + 1) + regen(v.slice(dash + 1))
}

/**
 * Engine number always keeps exactly one hyphen: left side is the (fixed)
 * engine code, right side is the serial whose digits are randomised.
 * When the source has no hyphen we split after the last letter and insert one.
 * When there is no letter at all (pure digits) the value is left untouched.
 */
const regenerateEngineNo = (value) => {
  const v = String(value ?? "").trim()
  if (!v) return v
  const dash = v.indexOf("-")
  if (dash !== -1) {
    return v.slice(0, dash + 1) + randomizeDigits(v.slice(dash + 1))
  }
  const lastAlpha = v.search(/[A-Za-z](?=[^A-Za-z]*$)/)
  if (lastAlpha === -1) return v
  const code = v.slice(0, lastAlpha + 1)
  const serial = v.slice(lastAlpha + 1)
  if (!/\d/.test(serial)) return v
  return `${code}-${randomizeDigits(serial)}`
}

/**
 * Bump a "MILEAGE 16,285 KM" style figure inside the Descriptions text by a
 * random 5,000–10,000 km (rounded to the nearest 100). Random per call so each
 * supportive row gets its own increase. No mileage text -> value untouched.
 */
const bumpMileageInText = (text) => {
  const raw = String(text ?? "")
  if (!/mileage/i.test(raw)) return raw
  const bump = Math.round((5000 + Math.random() * 5000) / 100) * 100
  return raw.replace(
    /(mileage[^0-9]*)(\d[\d,]*)(\s*)(km)/gi,
    (_, pre, num, sp, km) => {
      const next = parseInt(num.replace(/,/g, ""), 10) + bump
      return `${pre}${next.toLocaleString("en-US")}${sp}${km}`
    }
  )
}

const IDENTIFIER_KEYS = ["Chassis No", "Engine No"]

const randomizeIdentifierSpecs = (specs = [], seen) =>
  specs.map((s) => {
    if (s.key === "Descriptions") {
      const bumped = bumpMileageInText(s.value)
      return bumped === s.value ? s : { ...s, value: bumped }
    }
    if (!IDENTIFIER_KEYS.includes(s.key)) return s
    const base = String(s.value ?? "").trim()
    if (!base) return s
    const regen = s.key === "Engine No" ? regenerateEngineNo : regenerateIdentifier
    let cand = base
    for (let i = 0; i < 12 && (cand === base || seen.has(cand)); i += 1) {
      cand = regen(base)
    }
    seen.add(cand)
    return { ...s, value: cand }
  })

const padInitials = (name) => {
  const parts = String(name || "").match(/[A-Za-z0-9]+/g) || []
  const initials = parts.map((w) => w[0].toUpperCase()).join("")
  return initials || "SQ"
}

// Configured pad prefix (trailing -, /, spaces stripped) wins; else name initials.
const padReferencePrefix = (pad) => {
  const configured = String(pad?.default_reference_prefix || "")
    .trim()
    .replace(/[-/\s]+$/, "")
  return configured || padInitials(pad?.name)
}

const makeReferenceNo = (pad) =>
  `${padReferencePrefix(pad)}-${Math.floor(1000 + Math.random() * 9000)}`

const cryptoId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`)

const computeValidUntil = (mode, baseDate, value) => {
  const base = baseDate ? new Date(baseDate) : new Date()
  if (mode === "specific_date") {
    if (!value) return ""
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? "" : format(d, "yyyy-MM-dd")
  }
  const days = mode === "30_days" ? 30 : 30
  return format(addDays(base, days), "yyyy-MM-dd")
}

const toDateInput = (value, fallback = null) => {
  const raw = value || fallback
  const d = raw ? new Date(raw) : new Date()
  return Number.isNaN(d.getTime())
    ? format(new Date(), "yyyy-MM-dd")
    : format(d, "yyyy-MM-dd")
}

const buildEmptyRow = (quotation, seenTerms) => {
  const baseDate = toDateInput(
    quotation?.pdf_content?.quotation_date || quotation?.quotation_date
  )
  const parentSpecs = quotation?.pdf_content?.vehicle_specs ?? []
  const parentReg = getParentRegistrationCost(quotation)
  return {
    company_pad_id: "",
    reference_no: "",
    quotation_date: baseDate,
    validity: { mode: "1_month", value: "" },
    price_items: DEFAULT_PRICE_ITEMS(),
    payment_terms: pickPaymentTerms(seenTerms),
    vehicle_specs: buildVehicleSpecs(parentSpecs),
    total_amount: quotation?.pdf_content?.total_amount ?? "",
    amount_in_words: quotation?.pdf_content?.amount_in_words ?? "",
    registration_cost: parentReg > 0 ? parentReg : 0,
    expanded: true,
  }
}

const buildPrefilledRow = (quotation, pad, seenTerms) => ({
  ...buildEmptyRow(quotation),
  company_pad_id: String(pad.id),
  reference_no: makeReferenceNo(pad),
  payment_terms: pad.payment_terms_template || pickPaymentTerms(seenTerms),
})

const buildDefaultRows = (quotation, pads = []) => {
  const available = pads.slice(0, 2)
  const parentReg = getParentRegistrationCost(quotation)
  const bumps = distinctBumps(2)
  const seenIds = new Set()
  const seenTerms = new Set()
  const withBump = (row, bump) => {
    const price_items = buildBumpedPriceItems(quotation, bump)
    const total = sumPriceItems(price_items) + (parentReg > 0 ? parentReg : 0)
    return {
      ...row,
      price_items,
      vehicle_specs: randomizeIdentifierSpecs(row.vehicle_specs, seenIds),
      registration_cost: parentReg > 0 ? parentReg : 0,
      total_amount: total,
      amount_in_words: amountToWords(total),
    }
  }
  if (available.length === 0) {
    return [
      withBump(buildEmptyRow(quotation, seenTerms), bumps[0]),
      withBump(buildEmptyRow(quotation, seenTerms), bumps[1]),
    ]
  }
  if (available.length === 1) {
    return [
      withBump(buildPrefilledRow(quotation, available[0], seenTerms), bumps[0]),
      withBump(buildEmptyRow(quotation, seenTerms), bumps[1]),
    ]
  }
  return [
    withBump(buildPrefilledRow(quotation, available[0], seenTerms), bumps[0]),
    withBump(buildPrefilledRow(quotation, available[1], seenTerms), bumps[1]),
  ]
}

const SupportiveRowForm = ({
  row,
  rowIndex,
  totalRows,
  pads,
  padsById,
  baseQuotationDate,
  showRemove = false,
  onUpdate,
  onUpdateValidity,
  onUpdatePriceItem,
  onAddPriceItem,
  onRemovePriceItem,
  onUpdateVehicleSpec,
  onSetField,
  onRemoveRow,
  onToggleExpanded,
}) => {
  const selectedPad = padsById[String(row.company_pad_id)]
  const rowDate = row.quotation_date || baseQuotationDate
  const validityValue =
    row.validity.mode === "specific_date"
      ? row.validity.value
      : computeValidUntil(row.validity.mode, rowDate, row.validity.value)

  const applyChip = (days) => {
    const base = rowDate ? parseISO(rowDate) : new Date()
    const next = format(addDays(base, days), "yyyy-MM-dd")
    onUpdateValidity(rowIndex, { mode: "specific_date", value: next })
  }

  return (
    <div className="mb-2 border rounded p-3 bg-white">
      <div>
        <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2">
            {totalRows > 1 && (
              <Badge color="primary" pill>
                #{rowIndex + 1}
              </Badge>
            )}
            <strong>{selectedPad?.name || "Pad not selected"}</strong>
            {selectedPad && (
              <Badge
                color={
                  selectedPad.pad_layout === "letter"
                    ? "info"
                    : selectedPad.pad_layout === "box"
                    ? "warning"
                    : "secondary"
                }
                className="text-capitalize"
              >
                {selectedPad.pad_layout || "tabular"}
              </Badge>
            )}
          </div>
          <div className="d-flex gap-1">
            <Button
              color="light"
              size="sm"
              onClick={() => onToggleExpanded(rowIndex)}
              type="button"
            >
              <i
                className={`bx ${
                  row.expanded ? "bx-chevron-up" : "bx-chevron-down"
                } me-1`}
              />
              {row.expanded ? "Hide details" : "Show details"}
            </Button>
            {showRemove && totalRows > 2 && (
              <Button
                color="light"
                size="sm"
                className="text-danger"
                onClick={() => onRemoveRow(rowIndex)}
                type="button"
                title="Remove row"
              >
                <i className="mdi mdi-trash-can-outline" />
              </Button>
            )}
          </div>
        </div>

        <Row className="g-2">
          <Col md={4}>
            <FormGroup className="mb-2">
              <Label className="small mb-1">Company Pad</Label>
              <Input
                type="select"
                bsSize="sm"
                value={row.company_pad_id}
                onChange={e => {
                  const pad = padsById[e.target.value]
                  onUpdate(rowIndex, {
                    company_pad_id: e.target.value,
                    reference_no: pad ? makeReferenceNo(pad) : "",
                    payment_terms:
                      pad?.payment_terms_template || pickPaymentTerms(),
                  })
                }}
              >
                <option value="">Select pad...</option>
                {pads.map(pad => (
                  <option key={pad.id} value={pad.id}>
                    {pad.name}
                    {pad.pad_layout ? ` · ${pad.pad_layout}` : ""}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Col>
        </Row>

        <Collapse isOpen={row.expanded}>
          <hr className="my-2" />
          <Row className="g-2">
            <Col md={3}>
              <FormGroup className="mb-2">
                <Label className="small mb-1">Quotation Date</Label>
                <Input
                  type="date"
                  bsSize="sm"
                  value={row.quotation_date || ""}
                  onChange={e =>
                    onUpdate(rowIndex, { quotation_date: e.target.value })
                  }
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup className="mb-2">
                <Label className="small mb-1 d-flex justify-content-between align-items-center">
                  <span>Quotation / Ref. No</span>
                  {selectedPad && (
                    <Button
                      color="link"
                      size="sm"
                      className="p-0"
                      type="button"
                      title="Regenerate"
                      onClick={() =>
                        onUpdate(rowIndex, {
                          reference_no: makeReferenceNo(selectedPad),
                        })
                      }
                    >
                      <i className="bx bx-refresh" />
                    </Button>
                  )}
                </Label>
                <Input
                  type="text"
                  bsSize="sm"
                  value={row.reference_no}
                  placeholder={
                    selectedPad ? `${padReferencePrefix(selectedPad)}-…` : "Auto"
                  }
                  onChange={e =>
                    onUpdate(rowIndex, { reference_no: e.target.value })
                  }
                />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup className="mb-2">
                <Label className="small mb-1">Validity Mode</Label>
                <Input
                  type="select"
                  bsSize="sm"
                  value={row.validity.mode}
                  onChange={e =>
                    onUpdateValidity(rowIndex, {
                      mode: e.target.value,
                      value: "",
                    })
                  }
                >
                  {VALIDITY_MODES.map(m => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup className="mb-2">
                <Label className="small mb-1">
                  Valid Until{" "}
                  {validityValue && (
                    <span className="text-muted">({validityValue})</span>
                  )}
                </Label>
                <Input
                  type="date"
                  bsSize="sm"
                  disabled={row.validity.mode !== "specific_date"}
                  min={rowDate}
                  value={validityValue}
                  onChange={e =>
                    onUpdateValidity(rowIndex, {
                      mode: "specific_date",
                      value: e.target.value,
                    })
                  }
                />
                <div className="mt-1 d-flex flex-wrap gap-1">
                  {VALIDITY_CHIPS.map(chip => (
                    <Button
                      key={chip.label}
                      type="button"
                      color="outline-secondary"
                      size="sm"
                      onClick={() => applyChip(chip.days)}
                    >
                      {chip.label}
                    </Button>
                  ))}
                </div>
              </FormGroup>
            </Col>
          </Row>

          <FormGroup className="mb-2">
            <Label className="small mb-1 d-flex justify-content-between">
              <span>Price Items</span>
              <Button
                color="link"
                size="sm"
                className="p-0"
                type="button"
                onClick={() => onAddPriceItem(rowIndex)}
              >
                <i className="bx bx-plus me-1" />
                Add row
              </Button>
            </Label>
            <Table size="sm" className="mb-0 align-middle">
              <thead>
                <tr>
                  <th style={{ width: "70%" }}>Particulars</th>
                  <th>Amount (BDT)</th>
                  <th style={{ width: 40 }} />
                </tr>
              </thead>
              <tbody>
                {row.price_items.map(item => (
                  <tr key={item.id}>
                    <td>
                      <Input
                        type="text"
                        bsSize="sm"
                        value={item.description}
                        placeholder="e.g. Unit Price"
                        onChange={e =>
                          onUpdatePriceItem(rowIndex, item.id, {
                            description: e.target.value,
                          })
                        }
                      />
                    </td>
                    <td>
                      <Input
                        type="number"
                        bsSize="sm"
                        value={item.amount}
                        min="0"
                        step="0.01"
                        onChange={e =>
                          onUpdatePriceItem(rowIndex, item.id, {
                            amount: e.target.value,
                          })
                        }
                      />
                    </td>
                    <td className="text-end">
                      {row.price_items.length > 1 && (
                        <Button
                          color="link"
                          size="sm"
                          className="p-0 text-danger"
                          type="button"
                          onClick={() => onRemovePriceItem(rowIndex, item.id)}
                          title="Remove"
                        >
                          <i className="mdi mdi-close" />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </FormGroup>

          <Row className="g-2 mb-2">
            <Col xs={12} md={4}>
              <FormGroup className="mb-0">
                <Label className="small mb-1">Registration Cost (BDT)</Label>
                <Input
                  type="number"
                  bsSize="sm"
                  min="0"
                  step="0.01"
                  value={row.registration_cost ?? 0}
                  onChange={e =>
                    onUpdate(rowIndex, { registration_cost: e.target.value })
                  }
                />
              </FormGroup>
            </Col>
          </Row>

          <FormGroup className="mb-0">
            <Label className="small mb-1 d-flex justify-content-between align-items-center">
              <span>Payment Terms</span>
              <Button
                color="link"
                size="sm"
                className="p-0"
                type="button"
                title="Reset to default"
                onClick={() =>
                  onUpdate(rowIndex, {
                    payment_terms:
                      selectedPad?.payment_terms_template ||
                      DEFAULT_PAYMENT_TERMS,
                  })
                }
              >
                <i className="bx bx-reset" />
              </Button>
            </Label>
            <Input
              type="textarea"
              bsSize="sm"
              rows={2}
              value={row.payment_terms}
              placeholder={
                selectedPad?.payment_terms_template || DEFAULT_PAYMENT_TERMS
              }
              onChange={e => onUpdate(rowIndex, { payment_terms: e.target.value })}
            />
            <small className="text-muted d-block mt-1">
              Default: full payment before vehicle delivery
              {selectedPad?.payment_terms_template
                ? ` (overridden by ${selectedPad.name}'s template)`
                : ""}
              . Edit for this quotation only.
            </small>
            {selectedPad?.description_template && (
              <small className="text-muted d-block mt-1">
                Pad description template:{" "}
                <em>{selectedPad.description_template}</em>
              </small>
            )}
          </FormGroup>

          <hr className="my-3" />
          <h6 className="text-muted text-uppercase small mb-2">
            Vehicle Specs (editable)
          </h6>
          <p className="text-muted small mb-2">
            Inherited from the main quotation. Override per row to give each
            supportive its own look.
          </p>
          <Row className="g-2">
            {row.vehicle_specs?.map(spec => (
              <Col
                xs={12}
                sm={spec.key === "Descriptions" ? 12 : 6}
                md={spec.key === "Descriptions" ? 12 : 4}
                key={spec.key}
              >
                <FormGroup className="mb-2">
                  <Label className="small mb-1">{spec.key}</Label>
                  {spec.key === "Descriptions" ? (
                    <Input
                      type="textarea"
                      bsSize="sm"
                      rows={3}
                      value={spec.value || ""}
                      placeholder={spec.key}
                      onChange={e =>
                        onUpdateVehicleSpec(rowIndex, spec.key, e.target.value)
                      }
                    />
                  ) : (
                    <Input
                      type="text"
                      bsSize="sm"
                      value={spec.value || ""}
                      placeholder={spec.key}
                      onChange={e =>
                        onUpdateVehicleSpec(rowIndex, spec.key, e.target.value)
                      }
                    />
                  )}
                </FormGroup>
              </Col>
            ))}
          </Row>

          <Row className="g-2">
            <Col xs={12} md={4}>
              <FormGroup className="mb-0">
                <Label className="small mb-1">Total (BDT)</Label>
                <Input
                  type="number"
                  bsSize="sm"
                  min="0"
                  step="0.01"
                  value={row.total_amount ?? ""}
                  placeholder="Auto from price items"
                  onChange={e =>
                    onSetField(rowIndex, "total_amount", e.target.value)
                  }
                />
              </FormGroup>
            </Col>
            <Col xs={12} md={8}>
              <FormGroup className="mb-0">
                <Label className="small mb-1">In Words</Label>
                <Input
                  type="text"
                  bsSize="sm"
                  value={row.amount_in_words || ""}
                  placeholder="e.g. Eighty Five Lakh Taka Only"
                  onChange={e =>
                    onSetField(rowIndex, "amount_in_words", e.target.value)
                  }
                />
              </FormGroup>
            </Col>
          </Row>
        </Collapse>
      </div>
    </div>
  )
}

const buildPayloadFromRow = (row, baseQuotationDate, padsById) => {
  const pad = padsById[String(row.company_pad_id)] || {}
  const quotationDate = row.quotation_date || baseQuotationDate
  const validity = {
    mode: row.validity.mode,
    value:
      computeValidUntil(row.validity.mode, quotationDate, row.validity.value) ||
      null,
  }
  const vehicleSpecs = (row.vehicle_specs || [])
    .filter(spec => String(spec?.key ?? "").trim() !== "")
    .map(spec => ({
      key: spec.key,
      value: spec.value ?? "",
      type: spec.type || "text",
    }))
  const totalRaw = row.total_amount
  const totalAmount = totalRaw === "" || totalRaw == null
    ? null
    : Number(totalRaw)
  const amountInWords = String(row.amount_in_words ?? "").trim() || null
  return {
    company_pad_id: Number(row.company_pad_id),
    reference_no: row.reference_no?.trim() || null,
    quotation_date: quotationDate || null,
    validity,
    price_items: row.price_items
      .filter(p => (p.description || "").trim() !== "" || Number(p.amount))
      .map(p => ({
        description: p.description,
        amount: Number(p.amount) || 0,
      })),
    vehicle_specs: vehicleSpecs,
    total_amount: Number.isFinite(totalAmount) ? totalAmount : null,
    amount_in_words: amountInWords,
    payment_terms: row.payment_terms?.trim() || null,
    registration_cost:
      Number(row.registration_cost) > 0 ? Number(row.registration_cost) : null,
    pad_layout: pad.pad_layout || "tabular",
  }
}

const buildRowFromChild = (child) => {
  const pdf = child?.pdf_content || {}
  const validUntil = pdf?.valid_until
    ? format(new Date(pdf.valid_until), "yyyy-MM-dd")
    : ""
  const quotationDate = pdf?.quotation_date
    ? toDateInput(pdf.quotation_date)
    : toDateInput(child?.created_at)
  const priceItems = Array.isArray(pdf?.price_items) && pdf.price_items.length
    ? pdf.price_items.map(p => ({
        id: cryptoId(),
        description: p.description ?? "",
        amount: p.amount ?? 0,
      }))
    : DEFAULT_PRICE_ITEMS()
  const registrationCost = Number(pdf?.registration_cost) || 0
  const total =
    totalWithRegistration(priceItems, registrationCost) || pdf?.total_amount || ""
  return {
    company_pad_id: child?.company_pad_id ?? child?.company_pad?.id ?? "",
    reference_no: child?.reference_no || pdf?.quotation_no || "",
    quotation_date: quotationDate,
    validity: {
      mode: validUntil ? "specific_date" : "1_month",
      value: validUntil,
    },
    price_items: priceItems,
    payment_terms: child?.payment_terms || pdf?.payment_terms || "",
    vehicle_specs: buildVehicleSpecs(pdf?.vehicle_specs ?? []),
    total_amount: total,
    amount_in_words: pdf?.amount_in_words || amountToWords(total),
    registration_cost: registrationCost,
    expanded: true,
  }
}

const getSupportiveLoanInitialValues = (loanData, quotation) => {
  const stored = loanData?.loan_information || {}
  const customer = quotation?.customer || {}

  return {
    type: loanData?.type || "company",
    bank_name: stored?.bank_name || "",
    branch_name: stored?.branch_name || "",
    unit_price: stored?.unit_price ?? "",
    bank_paid: stored?.bank_paid ?? 0,
    customer_paid: stored?.customer_paid ?? 0,
    customer_name: stored?.customer_name || customer?.name || "",
    customer_address: stored?.customer_address || customer?.full_address || "",
    ac_hp_format: stored?.ac_hp_format || "H/P",
    bank_account_id: stored?.bank_account?.id ?? null,
    payorder_account_name: stored?.bank_account?.account_name ?? "",
    payorder_account_number: stored?.bank_account?.account_number ?? "",
    payorder_routing_number: stored?.bank_account?.routing_number ?? "",
    payorder_bank_name: stored?.bank_account?.bank_name ?? "",
    payorder_branch_name: stored?.bank_account?.branch_name ?? "",
    customer_payment_mode: stored?.customer_payment_mode || "",
    bank_payment_mode: stored?.bank_payment_mode || "",
    customer_payment_remarks: stored?.customer_payment_remarks || "",
    bank_payment_remarks: stored?.bank_payment_remarks || "",
    payorder_receiver_name: stored?.payorder_receiver_name || "",
    include_handover_block: Boolean(stored?.include_handover_block),
  }
}

  const SupportiveQuotations = ({ quotation, onChanged }) => {
  const { handleError } = useToastMessage()
  const children = quotation?.supportive_quotations ?? []

  const { data: padData } = useAllCompanyPads({ is_active: 1 })
  const pads = padData?.data?.data ?? padData?.data ?? []
  const padsById = useMemo(
    () => Object.fromEntries(pads.map(p => [String(p.id), p])),
    [pads]
  )

  const { mutateAsync: generate, isPending: isGenerating } =
    useGenerateSupportive()
  const { mutateAsync: updateSupportive, isPending: isUpdating } =
    useUpdateSupportiveQuotation()
  const { mutateAsync: deleteSupportive, isPending: isDeleting } =
    useDeleteSupportiveQuotation()
  const { mutateAsync: saveLoan, isPending: isSavingLoan } =
    useUpdateSupportiveLoan()

  const [generateOpen, setGenerateOpen] = useState(false)
  const [editTarget, setEditTarget] = useState(null)
  const [rows, setRows] = useState(() => [
    buildEmptyRow(quotation),
    buildEmptyRow(quotation),
  ])
  const [editRow, setEditRow] = useState(() => buildEmptyRow(quotation))
  const [downloadingId, setDownloadingId] = useState(null)
  const [deletingId, setDeletingId] = useState(null)
  const [loanTarget, setLoanTarget] = useState(null)
  const [activeDocumentKey, setActiveDocumentKey] = useState(null)

  const { data: loanResponse } = useSupportiveLoan(
    loanTarget?.id,
    !!loanTarget
  )
  const loanInitialValues = useMemo(
    () => getSupportiveLoanInitialValues(loanResponse?.data, quotation),
    [loanResponse, quotation]
  )

  const baseQuotationDate = useMemo(() => {
    const raw = quotation?.pdf_content?.quotation_date
    if (!raw) return format(new Date(), "yyyy-MM-dd")
    const d = new Date(raw)
    return Number.isNaN(d.getTime())
      ? format(new Date(), "yyyy-MM-dd")
      : format(d, "yyyy-MM-dd")
  }, [quotation])

  const updateRow = (idx, patch) =>
    setRows(prev =>
      prev.map((r, i) => {
        if (i !== idx) return r
        const next = { ...r, ...patch }
        if (Object.prototype.hasOwnProperty.call(patch, "registration_cost")) {
          next.total_amount = totalWithRegistration(
            next.price_items,
            next.registration_cost
          )
          next.amount_in_words = amountToWords(next.total_amount)
        }
        return next
      })
    )

  const updateValidity = (idx, patch) =>
    setRows(prev =>
      prev.map((r, i) =>
        i === idx ? { ...r, validity: { ...r.validity, ...patch } } : r
      )
    )

  const updatePriceItem = (rowIdx, itemId, patch) =>
    setRows(prev =>
      prev.map((r, i) => {
        if (i !== rowIdx) return r
        const price_items = r.price_items.map(p =>
          p.id === itemId ? { ...p, ...patch } : p
        )
        const total_amount = totalWithRegistration(
          price_items,
          r.registration_cost
        )
        return {
          ...r,
          price_items,
          total_amount,
          amount_in_words: amountToWords(total_amount),
        }
      })
    )

  const addPriceItem = rowIdx =>
    setRows(prev =>
      prev.map((r, i) => {
        if (i !== rowIdx) return r
        const price_items = [
          ...r.price_items,
          { id: cryptoId(), description: "", amount: 0 },
        ]
        const total_amount = totalWithRegistration(
          price_items,
          r.registration_cost
        )
        return {
          ...r,
          price_items,
          total_amount,
          amount_in_words: amountToWords(total_amount),
        }
      })
    )

  const removePriceItem = (rowIdx, itemId) =>
    setRows(prev =>
      prev.map((r, i) => {
        if (i !== rowIdx) return r
        const price_items = r.price_items.filter(p => p.id !== itemId)
        const total_amount = totalWithRegistration(
          price_items,
          r.registration_cost
        )
        return {
          ...r,
          price_items,
          total_amount,
          amount_in_words: amountToWords(total_amount),
        }
      })
    )

  const updateVehicleSpec = (rowIdx, key, value) =>
    setRows(prev =>
      prev.map((r, i) => {
        if (i !== rowIdx) return r
        return {
          ...r,
          vehicle_specs: r.vehicle_specs.map(s =>
            s.key === key ? { ...s, value } : s
          ),
        }
      })
    )

  const setRowField = (rowIdx, key, value) =>
    setRows(prev =>
      prev.map((r, i) => (i === rowIdx ? { ...r, [key]: value } : r))
    )

  const addRow = () =>
    setRows(prev => {
      const seen = new Set()
      prev.forEach(r =>
        (r.vehicle_specs || []).forEach(s => {
          if (IDENTIFIER_KEYS.includes(s.key) && s.value) {
            seen.add(String(s.value).trim())
          }
        })
      )
      const seenTerms = new Set(prev.map(r => r.payment_terms).filter(Boolean))
      const row = buildEmptyRow(quotation, seenTerms)
      row.vehicle_specs = randomizeIdentifierSpecs(row.vehicle_specs, seen)
      return [...prev, row]
    })
  const removeRow = idx => setRows(prev => prev.filter((_, i) => i !== idx))
  const toggleExpanded = idx =>
    updateRow(idx, { expanded: !rows[idx].expanded })

  const openGenerate = () => {
    setRows(buildDefaultRows(quotation, pads))
    setGenerateOpen(true)
  }

  const openEdit = useCallback(child => {
    setEditTarget(child)
    setEditRow(buildRowFromChild(child))
  }, [])

  const closeEdit = () => {
    setEditTarget(null)
    setEditRow(buildEmptyRow(quotation))
  }

  const updateEditRow = (_idx, patch) =>
    setEditRow(prev => {
      const next = { ...prev, ...patch }
      if (Object.prototype.hasOwnProperty.call(patch, "registration_cost")) {
        next.total_amount = totalWithRegistration(
          next.price_items,
          next.registration_cost
        )
        next.amount_in_words = amountToWords(next.total_amount)
      }
      return next
    })

  const updateEditValidity = (_idx, patch) =>
    setEditRow(prev => ({
      ...prev,
      validity: { ...prev.validity, ...patch },
    }))

  const updateEditPriceItem = (itemId, patch) =>
    setEditRow(prev => {
      const price_items = prev.price_items.map(p =>
        p.id === itemId ? { ...p, ...patch } : p
      )
      const total_amount = totalWithRegistration(
        price_items,
        prev.registration_cost
      )
      return {
        ...prev,
        price_items,
        total_amount,
        amount_in_words: amountToWords(total_amount),
      }
    })

  const updateEditVehicleSpec = (key, value) =>
    setEditRow(prev => ({
      ...prev,
      vehicle_specs: prev.vehicle_specs.map(s =>
        s.key === key ? { ...s, value } : s
      ),
    }))

  const setEditField = (key, value) =>
    setEditRow(prev => ({ ...prev, [key]: value }))

  const addEditPriceItem = () =>
    setEditRow(prev => {
      const price_items = [
        ...prev.price_items,
        { id: cryptoId(), description: "", amount: 0 },
      ]
      const total_amount = totalWithRegistration(
        price_items,
        prev.registration_cost
      )
      return {
        ...prev,
        price_items,
        total_amount,
        amount_in_words: amountToWords(total_amount),
      }
    })

  const removeEditPriceItem = itemId =>
    setEditRow(prev => {
      const price_items = prev.price_items.filter(p => p.id !== itemId)
      const total_amount = totalWithRegistration(
        price_items,
        prev.registration_cost
      )
      return {
        ...prev,
        price_items,
        total_amount,
        amount_in_words: amountToWords(total_amount),
      }
    })

  const toggleEditExpanded = () =>
    setEditRow(prev => ({ ...prev, expanded: !prev.expanded }))

  const handleDownload = useCallback(async child => {
    setDownloadingId(child.id)
    try {
      await downloadAndOpenFile({
        url: `/supportive-quotations/${child.id}/pdf`,
      })
    } catch (error) {
      handleError(error, "Failed to download PDF.")
    } finally {
      setDownloadingId(null)
    }
  }, [handleError])

  const openLoanModal = useCallback(child => {
    setLoanTarget(child)
  }, [])

  const closeLoanModal = () => {
    setLoanTarget(null)
    setActiveDocumentKey(null)
  }

  const handleSaveLoan = async payload => {
    if (!loanTarget) return
    try {
      await saveLoan({ id: loanTarget.id, ...payload })
      toast.success("Loan information saved.")
    } catch (error) {
      handleError(error, "Failed to save loan information.")
    }
  }

  const handleDocumentDownload = async documentKey => {
    if (!loanTarget) return
    setActiveDocumentKey(documentKey)
    try {
      await downloadAndOpenFile({
        url: `/supportive-quotations/${loanTarget.id}/document/${documentKey}`,
      })
    } catch (error) {
      handleError(error, "Failed to generate document.")
    } finally {
      setActiveDocumentKey(null)
    }
  }

  const handleDelete = useCallback(async child => {
    if (
      !window.confirm(`Delete supportive quotation ${child.sqid ?? child.id}?`)
    ) {
      return
    }
    setDeletingId(child.id)
    try {
      await deleteSupportive(child.id)
      toast.success("Supportive quotation deleted.")
      onChanged?.()
    } catch (error) {
      handleError(
        error,
        "Failed to delete supportive quotation."
      )
    } finally {
      setDeletingId(null)
    }
  }, [deleteSupportive, handleError, onChanged])

  const handleGenerate = async () => {
    if (rows.length < 2) {
      toast.error("At least two supportive quotations are required.")
      return
    }
    for (const [idx, row] of rows.entries()) {
      if (!row.company_pad_id) {
        toast.error(`Select a company pad for row ${idx + 1}.`)
        return
      }
      if (row.validity.mode === "specific_date" && !row.validity.value) {
        toast.error(`Pick a specific valid-until date for row ${idx + 1}.`)
        return
      }
    }

    const items = rows.map(r =>
      buildPayloadFromRow(r, baseQuotationDate, padsById)
    )

    try {
      await generate({ id: quotation.qid ?? quotation.id, items })
      toast.success("Supportive quotations generated.")
      setGenerateOpen(false)
      onChanged?.()
    } catch (error) {
      handleError(error, "Failed to generate supportive quotations.")
    }
  }

  const handleUpdate = async () => {
    if (!editTarget) return
    if (!editRow.company_pad_id) {
      toast.error("Select a company pad.")
      return
    }
    if (
      editRow.validity.mode === "specific_date" &&
      !editRow.validity.value
    ) {
      toast.error("Pick a specific valid-until date.")
      return
    }

    const payload = buildPayloadFromRow(
      editRow,
      baseQuotationDate,
      padsById
    )

    try {
      await updateSupportive({ id: editTarget.id, ...payload })
      toast.success("Supportive quotation updated.")
      closeEdit()
      onChanged?.()
    } catch (error) {
      handleError(error, "Failed to update supportive quotation.")
    }
  }

  return (
    <Card className="mt-3">
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-3 gap-2 flex-wrap">
          <h5 className="mb-0">
            Total{" "}
            <span className="badge badge-soft-info">{children.length}</span>
          </h5>
          <Button color="primary" size="sm" onClick={openGenerate}>
            <i className="bx bx-plus me-1" />
            <span className="d-none d-sm-inline">Generate Supportive</span>
          </Button>
        </div>

        {children.length === 0 ? (
          <p className="text-muted mb-0">
            No supportive quotations yet. Generate at least two on different
            company pads.
          </p>
        ) : (
          <div className="table-responsive">
            <Table
              hover
              className="align-middle dt-responsive w-100 table-check dataTable no-footer dtr-inline mt-2 border-top"
            >
              <thead className="table-light">
                <tr>
                  <th>Quotation</th>
                  <th>Company Pad</th>
                  <th className="d-none d-md-table-cell">Layout</th>
                  <th className="d-none d-lg-table-cell">Vehicle</th>
                  <th className="d-none d-md-table-cell">Amount</th>
                  <th className="d-none d-sm-table-cell">Status</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {children.map(child => (
                  <tr key={child.id}>
                    <td>
                      <div
                        style={{
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                        }}
                      >
                        <div className="fw-bold text-primary mb-1">
                          {child?.sqid ??
                            child?.reference_no ??
                            `#${child?.id}`}
                        </div>
                        {child?.pdf_content?.valid_until && (
                          <small className="text-muted d-block">
                            Valid until{" "}
                            {format(
                              new Date(child.pdf_content.valid_until),
                              "MMM d, y"
                            )}
                          </small>
                        )}
                        {child?.pdf_content?.quotation_no &&
                          child.pdf_content.quotation_no !==
                            child?.sqid && (
                            <small className="text-muted d-block">
                              Ref: {child.pdf_content.quotation_no}
                            </small>
                          )}
                      </div>
                    </td>
                    <td>
                      <div
                        style={{
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                        }}
                      >
                        <div className="fw-semibold">
                          {child?.company_pad?.name ?? "-"}
                        </div>
                        {child?.company_pad?.short_name && (
                          <small className="text-muted d-block">
                            {child.company_pad.short_name}
                          </small>
                        )}
                      </div>
                    </td>
                    <td className="d-none d-md-table-cell">
                      <Badge color="secondary" className="text-capitalize">
                        {child?.company_pad?.pad_layout || "tabular"}
                      </Badge>
                    </td>
                    <td className="d-none d-lg-table-cell">
                      <div className="small">
                        <div>
                          {findSpecValue(
                            child?.pdf_content?.vehicle_specs ?? [],
                            "Vehicle Name"
                          ) || "-"}
                        </div>
                        {findSpecValue(
                          child?.pdf_content?.vehicle_specs ?? [],
                          "Chassis No"
                        ) && (
                          <small className="text-muted d-block">
                            {findSpecValue(
                              child?.pdf_content?.vehicle_specs ?? [],
                              "Chassis No"
                            )}
                          </small>
                        )}
                      </div>
                    </td>
                    <td className="d-none d-md-table-cell">
                      <div className="fw-semibold">
                        {child?.pdf_content?.total_amount != null &&
                        child.pdf_content.total_amount !== ""
                          ? child.pdf_content.total_amount
                          : "-"}
                      </div>
                    </td>
                    <td className="d-none d-sm-table-cell">
                      <Badge color="info">
                        {child?.status?.name ?? "Pending"}
                      </Badge>
                    </td>
                    <td className="text-end">
                      <ul className="list-unstyled d-flex flex-wrap gap-1 mb-0">
                        <li>
                          <ButtonTooltip
                            type="edit"
                            btnId={`supportive-edit-${child?.id}`}
                            onClick={() => openEdit(child)}
                          />
                        </li>
                        <li>
                          <ButtonTooltip
                            type="pdf"
                            btnId={`supportive-pdf-${child?.id}`}
                            loading={downloadingId === child?.id}
                            onClick={() => handleDownload(child)}
                          />
                        </li>
                        <li>
                          <ButtonTooltip
                            type="receipt"
                            btnId={`supportive-loan-${child?.id}`}
                            btnName="Loan Info & Documents"
                            onClick={() => openLoanModal(child)}
                          />
                        </li>
                        <li>
                          <ButtonTooltip
                            type="delete"
                            btnId={`supportive-delete-${child?.id}`}
                            loading={deletingId === child?.id}
                            onClick={() => handleDelete(child)}
                          />
                        </li>
                      </ul>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </CardBody>

      {/* Generate Modal */}
      <Modal
        isOpen={generateOpen}
        toggle={() => setGenerateOpen(false)}
        size="xl"
        centered
      >
        <ModalHeader toggle={() => setGenerateOpen(false)}>
          Generate Supportive Quotations
        </ModalHeader>
        <ModalBody>
          {pads.length === 0 && (
            <div className="alert alert-warning">
              No active company pads found. Create one under Quotation → Company
              Pads first.
            </div>
          )}

          {rows.map((row, idx) => (
            <SupportiveRowForm
              key={`gen-${idx}`}
              row={row}
              rowIndex={idx}
              totalRows={rows.length}
              pads={pads}
              padsById={padsById}
              baseQuotationDate={baseQuotationDate}
              showRemove
              onUpdate={updateRow}
              onUpdateValidity={updateValidity}
              onUpdatePriceItem={updatePriceItem}
              onAddPriceItem={addPriceItem}
              onRemovePriceItem={removePriceItem}
              onUpdateVehicleSpec={updateVehicleSpec}
              onSetField={setRowField}
              onRemoveRow={removeRow}
              onToggleExpanded={toggleExpanded}
            />
          ))}

          <Button color="link" size="sm" onClick={addRow} type="button">
            <i className="bx bx-plus me-1" />
            Add Row
          </Button>
          <p className="text-muted small mb-0">Minimum two required.</p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            onClick={() => setGenerateOpen(false)}
            type="button"
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleGenerate}
            disabled={isGenerating || pads.length === 0}
            type="button"
          >
            {isGenerating ? "Generating..." : "Generate"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={!!editTarget}
        toggle={closeEdit}
        size="xl"
        centered
        unmountOnClose
        keyboard
        backdrop="static"
      >
        <ModalHeader toggle={closeEdit}>
          Edit Supportive Quotation
          {editTarget && (
            <span className="ms-2 text-muted small">
              {editTarget.sqid ?? editTarget.reference_no ?? `#${editTarget.id}`}
            </span>
          )}
        </ModalHeader>
        <ModalBody>
          {pads.length === 0 && (
            <div className="alert alert-warning">
              No active company pads found. Create one under Quotation → Company
              Pads first.
            </div>
          )}
          <SupportiveRowForm
            key={editTarget ? `edit-${editTarget.id}` : "edit-empty"}
            row={editRow}
            rowIndex={0}
            totalRows={1}
            pads={pads}
            padsById={padsById}
            baseQuotationDate={baseQuotationDate}
            onUpdate={updateEditRow}
            onUpdateValidity={updateEditValidity}
            onUpdatePriceItem={(_, itemId, patch) =>
              updateEditPriceItem(itemId, patch)
            }
            onAddPriceItem={() => addEditPriceItem()}
            onRemovePriceItem={(_, itemId) => removeEditPriceItem(itemId)}
            onUpdateVehicleSpec={(_, key, value) =>
              updateEditVehicleSpec(key, value)
            }
            onSetField={(_, key, value) => setEditField(key, value)}
            onToggleExpanded={() => toggleEditExpanded()}
          />
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={closeEdit} type="button">
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleUpdate}
            disabled={isUpdating || !editTarget}
            type="button"
          >
            {isUpdating ? "Saving..." : "Save Changes"}
          </Button>
        </ModalFooter>
      </Modal>

      <SupportiveLoanAddEdit
        modal={!!loanTarget}
        toggleModal={closeLoanModal}
        supportive={loanTarget}
        initialValues={loanInitialValues}
        onSubmit={handleSaveLoan}
        isSubmitting={isSavingLoan}
        activeDocumentKey={activeDocumentKey}
        onDocumentDownload={handleDocumentDownload}
      />
    </Card>
  )
}

export default SupportiveQuotations
